<script lang="ts">
  import type { Models } from '@smartlinkdev/types';
  import { onMount } from 'svelte';
  import { amenities, mapInstance, venue } from '$lib/stores';
  import { activeLanguage, activeTranslations, translations, multiLingualEnabled } from '$lib/stores/languages';
  import { getTranslation, t } from '$lib/translations';
  import type { AmenityTypeListing } from '$lib/types';

  let activeAmenityId: number | undefined;
  let amenityTypes: AmenityTypeListing[] = [];
  let isLoading = true;

  const translatedAmenityTypeName = (amenityType: AmenityTypeListing): string => {
    if ($multiLingualEnabled) {
      const found = getTranslation({
        modelId: amenityType.id,
        modelAttribute: 'name',
        modelTable: 'categories',
      });
      if (found) {
        return found.text;
      }
    }
    return amenityType.name;
  }

  const extractAmenityTypesFromAmenities = () => {
    const customPins = $venue?.settings?.amenitySettings?.customPins || {};
    const types: AmenityTypeListing[] = [];
    $amenities.forEach((amenity) => {
      if (amenity.amenity_types) {
        amenity.amenity_types.forEach((aType: Models.Category) => {
          const find = types.findIndex((type) => type.id === aType.id);
          if (find === -1) {
            types.push({
              id: aType.id,
              name: translatedAmenityTypeName(aType),
              icon: customPins[aType.id] || '',
              count: 1,
            });
          } else if (find >= 0) {
            types[find].count += 1;
          }
        });
      }
    });

    amenityTypes = types;
    isLoading = false;
  };

  const amenityTypeClick = (amenityTypeId: number) => {
    if ($mapInstance) {
      if (amenityTypeId > 0) {
        $mapInstance.showAmenityType(amenityTypeId);
        activeAmenityId = amenityTypeId;
        const zoomLevel = $venue.settings.mapboxSettings.minZoom;
        $mapInstance.showFullMap(zoomLevel);
      } else {
        $mapInstance.resetAmenities();
      }
    }
  };

  activeTranslations.subscribe((translations: Models.Translation[]) => {
    extractAmenityTypesFromAmenities();
  });

  onMount(() => {
    extractAmenityTypesFromAmenities();
  });
</script>

<div class="amenities-listing">
  <div class="amenities-subtitle">{ $t('content.amenities') }</div>
  {#if isLoading}
    <div>{ $t('content.loading') }</div>
  {:else if amenityTypes.length > 0}
    {#each amenityTypes as amenityType (amenityType.id)}
      <div
        class="amenity-listing-item"
        on:click={() => {
          amenityTypeClick(amenityType.id);
        }}
        on:keydown={() => {
          // TODO
        }}
      >
        <img
          src={amenityType.icon}
          alt={`${amenityType.name} Icon`}
          class="amenity-icon"
          class:active={amenityType.id === activeAmenityId}
        />
        <div class="amenity-name">{amenityType.name}</div>
        <div class="amenity-count">{amenityType.count}</div>
      </div>
    {/each}
  {/if}
</div>

<style lang="postcss">
  .amenities-listing {
    @apply overflow-y-auto bg-white mt-8 h-[22.5vh];
  }

  .amenities-subtitle {
    @apply h-5 text-gray-300 text-xs;
  }

  .amenity-listing-item {
    @apply h-6 text-xs flex items-center my-3;
    font-weight: 500;
  }

  .amenity-listing-item:hover {
    @apply font-bold cursor-pointer;
  }

  .amenity-icon {
    @apply h-6 w-6 p-0;
  }

  .amenity-name {
    @apply ml-2 mr-4;
  }

  .amenity-count {
    @apply bg-gray-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-xl;
  }

</style>
